import React, { Suspense, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ShadeDialog";
import { SlapReviewerContent, SlapReviewerLoading } from './SlapReviewerContent';
import { Board, StickerDesignId, SidebarMode, ObjectiveId } from '../utils/Primitives';
import { PlaceId } from '../utils/Primitives';
import { Address } from 'viem';
import GetTheLink from '../components/GetTheLink';
import { pridi } from '../fonts';

type SlapReviewerProps = {
  slapId: number | null;
  boardRef: React.MutableRefObject<Board>;
  onClose: () => void;
  handleReviewPlace: (placeId: PlaceId) => void;
  handleReviewPlaceOnly: (placeId: PlaceId) => void;
  handleReviewSticker: (stickerDesignId: StickerDesignId) => void;
  handleReviewPlayer: (playerAddress: Address) => void;
  setActiveSidebarMode?: (mode: SidebarMode) => void;
  setSidebarToObjectives: (objectiveId: ObjectiveId | null) => void;
};

export default function SlapReviewer({
  slapId,
  boardRef,
  onClose,
  handleReviewPlace,
  handleReviewPlaceOnly,
  handleReviewSticker,
  handleReviewPlayer,
  setActiveSidebarMode,
  setSidebarToObjectives,
}: SlapReviewerProps) {
  const handleReviewPlaceAndClose = useCallback((placeId: PlaceId) => {
    handleReviewPlace(placeId);
    onClose();
  }, [handleReviewPlace, onClose]);

  // Add a new handler that doesn't close
  const handleReviewPlaceNoClose = useCallback((placeId: PlaceId) => {
    handleReviewPlaceOnly(placeId);
  }, [handleReviewPlaceOnly]);

  if (slapId === null) {
    return null; // Hide the dialog if no slapId is provided
  }

  return (
    <Dialog open={slapId !== null} onOpenChange={onClose}>
      <DialogContent className='bg-white text-slate-900 p-4'>
        <DialogHeader>
          <div className="flex items-center gap-2">
            <DialogTitle className={`text-2xl ${pridi.className}`}>Slap #{slapId}</DialogTitle>
            <GetTheLink placeId={null} slapId={slapId} stickerDesignId={null} />
          </div>
          <Suspense fallback={<SlapReviewerLoading />}>
            <SlapReviewerContent
              slapId={slapId}
              boardRef={boardRef}
              onClose={onClose}
              handleReviewPlace={handleReviewPlaceAndClose}
              handleReviewPlaceOnly={handleReviewPlaceNoClose}
              handleReviewSticker={handleReviewSticker}
              handleReviewPlayer={handleReviewPlayer}
              setActiveSidebarMode={setActiveSidebarMode}
              setSidebarToObjectives={setSidebarToObjectives}
            />
          </Suspense>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
}
